/**
 * usePermissions — typed reactive convenience getters around permissionsService
 *
 * Backed by permissionsService.isUserInGroups, which reads the current
 * permissionsState.user.groups. The Cognito group cascade (Full → Franqueado
 * → Gerente → Operador → Atendente → Cliente) means a Full user is also in
 * the Atendente group — so `isAtendenteOrOperador` MUST exclude managers
 * explicitly, otherwise a Full user would be classified as Atendente and
 * hit the valor-restante cap meant for Atendente/Operador only.
 *
 * All getters are readonly computeds (deliberate — caller code should never
 * mutate these). Use as:
 *
 *   const { isManagerOrAbove, canCancelOS } = useUserPermissions();
 *   v-if="canCancelOS"
 *
 * feature-agnostic — permissions are runtime user state, not behind a build-time flag.
 */
import { computed, type ComputedRef } from 'vue';
import { permissionsService } from '../services/permissionsService';

export interface UserPermissions {
  /** Full, Franqueado, or Gerente — the "manager and above" tier. */
  isManagerOrAbove: ComputedRef<boolean>;
  /** Atendente or Operador *and not* manager-or-above. The exclusion is required
   *  because the Cognito group cascade means Full users are also in Atendente. */
  isAtendenteOrOperador: ComputedRef<boolean>;
  /** Group half of the cancel-OS rule — Full/Franqueado/Gerente/Atendente,
   *  mirroring the group gate in so/orders.php::cancel. Deliberately NOT the PDV
   *  quintet: Operador was removed from cancel on 2026-08-10 because cancelling
   *  is a financial act, so it is excluded here too. The backend layers a second
   *  condition on top that this composable cannot evaluate — Atendente may only
   *  cancel an order with zero recebimentos. Callers that know the order must AND
   *  this with that check (see PDVCheckoutForm). */
  canCancelOS: ComputedRef<boolean>;
  /** Group half of the uncancel-OS rule — Full/Franqueado/Gerente/Atendente,
   *  mirroring the group gate in so/orders.php::uncancel (lines 1088-1091);
   *  see SoOrdersUncancelPermissionTest for the executable spec. Unlike
   *  canCancelOS, there is NO second condition on top: an Atendente may
   *  uncancel a paid order (the backend imposes no recebimento floor here).
   *  Deliberately NOT sharing the array with canCancelOS — these are two
   *  independent product gates that happen to coincide today; the backend
   *  keeps them separate. */
  canUncancelOS: ComputedRef<boolean>;
  /** Can edit existing recebimentos. Mirrors backend gate in recebimentos.php::save (update path). */
  canEditRecebimento: ComputedRef<boolean>;
  /** Sell or use a prepaid combo — Full/Franqueado/Gerente/Atendente, mirroring
   *  so/orders.php::comboUseAllowed. Counter acts: Operador (production) is out. */
  canUseCombos: ComputedRef<boolean>;
}

export function useUserPermissions(): UserPermissions {
  const isManagerOrAbove = computed<boolean>(() =>
    permissionsService.isUserInGroups(['Full', 'Franqueado', 'Gerente'])
  );

  // Intentional: subtract manager-or-above. Cognito groups cascade — a Full
  // user is ALSO in Atendente / Operador, so the naive isUserInGroups check
  // would mark them as Atendente. The cap rules only apply to the lower tier.
  const isAtendenteOrOperador = computed<boolean>(() =>
    !isManagerOrAbove.value &&
    permissionsService.isUserInGroups(['Atendente', 'Operador'])
  );

  const canCancelOS = computed<boolean>(() =>
    permissionsService.isUserInGroups(['Full', 'Franqueado', 'Gerente', 'Atendente'])
  );
  const canUncancelOS = computed<boolean>(() =>
    permissionsService.isUserInGroups(['Full', 'Franqueado', 'Gerente', 'Atendente'])
  );
  const canEditRecebimento = computed<boolean>(() => isManagerOrAbove.value);
  const canUseCombos = computed<boolean>(() =>
    permissionsService.isUserInGroups(['Full', 'Franqueado', 'Gerente', 'Atendente'])
  );

  return {
    isManagerOrAbove,
    isAtendenteOrOperador,
    canCancelOS,
    canUncancelOS,
    canEditRecebimento,
    canUseCombos,
  };
}
